import { useEffect, useRef, useState } from 'react';

const SECTION_IDS = ['crisis', 'science', 'pillars', 'scaffold', 'scout', 'market', 'vision', 'ask'];

export function NavBar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navTabsRef = useRef<HTMLElement | null>(null);
  const suppressChange = useRef(false);

  useEffect(() => {
    const hero = document.getElementById('hero');
    if (!hero) return;

    const scrollObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          setScrolled(!entry.isIntersecting);
        }
      },
      { threshold: 0, rootMargin: '-72px 0px 0px 0px' },
    );

    scrollObserver.observe(hero);
    return () => scrollObserver.disconnect();
  }, []);

  useEffect(() => {
    const sections = SECTION_IDS
      .map((id) => document.getElementById(id))
      .filter(Boolean) as HTMLElement[];

    const sectionObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const id = entry.target.id;
          const tabIndex = SECTION_IDS.indexOf(id);
          const tabs = navTabsRef.current;
          if (tabIndex >= 0 && tabs) {
            suppressChange.current = true;
            (tabs as any).activeTabIndex = tabIndex;
            requestAnimationFrame(() => {
              suppressChange.current = false;
            });
          }
        }
      },
      { rootMargin: '-40% 0px -50% 0px', threshold: 0 },
    );

    sections.forEach((s) => sectionObserver.observe(s));
    return () => sectionObserver.disconnect();
  }, []);

  useEffect(() => {
    const tabs = navTabsRef.current;
    if (!tabs) return;

    const handleChange = () => {
      if (suppressChange.current) return;
      const index = (tabs as any).activeTabIndex;
      const targetId = SECTION_IDS[index];
      const target = document.getElementById(targetId);
      if (target) target.scrollIntoView({ behavior: 'smooth' });
    };

    tabs.addEventListener('change', handleChange);
    return () => tabs.removeEventListener('change', handleChange);
  }, []);

  function closeMobile() {
    setMobileOpen(false);
  }

  return (
    <header className={`nav-bar ${scrolled ? 'scrolled' : ''}`} id="navBar">
      <div className="nav-inner">
        <a href="#hero" className="nav-brand">eduksy.</a>

        <nav className="nav-tabs-wrapper" id="navTabsWrapper" aria-label="Section navigation">
          <md-tabs ref={navTabsRef} id="navTabs" aria-label="Page sections">
            <md-primary-tab data-section="crisis">The Crisis</md-primary-tab>
            <md-primary-tab data-section="science">The Science</md-primary-tab>
            <md-primary-tab data-section="pillars">Three Pillars</md-primary-tab>
            <md-primary-tab data-section="scaffold">Scaffold</md-primary-tab>
            <md-primary-tab data-section="scout">Scout AI</md-primary-tab>
            <md-primary-tab data-section="market">The Market</md-primary-tab>
            <md-primary-tab data-section="vision">Vision</md-primary-tab>
            <md-primary-tab data-section="ask">The Ask</md-primary-tab>
          </md-tabs>
        </nav>

        <md-filled-button className="nav-cta" href="/demo">
          Experience the Demo
        </md-filled-button>

        <button
          className="nav-hamburger"
          id="navHamburger"
          aria-label="Open navigation menu"
          onClick={() => setMobileOpen((prev) => !prev)}
        >
          <span className="material-symbols-outlined">menu</span>
        </button>
      </div>

      {/* Mobile overlay */}
      <div
        className={`mobile-nav-overlay ${mobileOpen ? 'open' : ''}`}
        id="mobileNavOverlay"
        style={{ display: mobileOpen ? 'block' : 'none' }}
        onClick={(e) => { if (e.target === e.currentTarget) closeMobile(); }}
      >
        <div className="mobile-nav-list">
          {SECTION_IDS.map((id) => (
            <a key={id} href={`#${id}`} className="mobile-nav-link" onClick={closeMobile}>
              {id === 'crisis' ? 'The Crisis' :
               id === 'science' ? 'The Science' :
               id === 'pillars' ? 'Three Pillars' :
               id === 'scaffold' ? 'Scaffold' :
               id === 'scout' ? 'Scout AI' :
               id === 'market' ? 'The Market' :
               id === 'vision' ? 'Vision' : 'The Ask'}
            </a>
          ))}
          <md-divider></md-divider>
          <a href="/demo" className="mobile-nav-link mobile-nav-cta" onClick={closeMobile}>Demo Hub</a>
          <a href="/arena" className="mobile-nav-link" onClick={closeMobile}>Arena Classroom</a>
          <a href="/sandbox" className="mobile-nav-link" onClick={closeMobile}>Concept Sandbox</a>
          <a href="/pitch-deck" className="mobile-nav-link" onClick={closeMobile}>View Pitch Deck</a>
        </div>
      </div>
    </header>
  );
}
